

/**
 * AppErrorBoundary - Catches runtime errors from apps
 *
 * If an app crashes, the Shell continues working.
 * Shows error UI with retry and close options.
 */

import React from 'react'
import { colors, typography, spacing, borderRadius } from '@/lib/tokens'

interface AppErrorBoundaryProps {
  children: React.ReactNode
  appId: string
  onRetry?: () => void
  onRemove?: () => void
}

interface AppErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[AppErrorBoundary] ${this.props.appId} crashed:`, error, errorInfo)
    this.setState({ errorInfo })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    this.props.onRetry?.()
  }

  handleRemove = () => {
    this.props.onRemove?.()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        background: colors.background,
        gap: spacing.lg,
        padding: spacing.xl,
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: borderRadius.full,
          background: 'rgba(255, 59, 59, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
        }}>
          !
        </div>

        <div style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.error,
          textAlign: 'center',
          fontFamily: typography.fontFamily.sans,
        }}>
          La aplicacion encontro un error
        </div>

        <div style={{
          fontSize: typography.fontSize.base,
          color: colors.textSecondary,
          textAlign: 'center',
          maxWidth: '500px',
          fontFamily: typography.fontFamily.sans,
        }}>
          {this.state.error?.message || 'Error desconocido'}
        </div>

        <button
          onClick={() => {
            const text = [
              this.state.error?.stack || '',
              this.state.errorInfo?.componentStack || '',
            ].filter(Boolean).join('\n\n')
            navigator.clipboard.writeText(text).then(() => {
              const btn = document.getElementById('copy-error-btn')
              if (btn) btn.textContent = 'Copiado!'
              setTimeout(() => {
                if (btn) btn.textContent = 'Copiar detalles tecnicos'
              }, 2000)
            })
          }}
          id="copy-error-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            padding: `${spacing.sm} ${spacing.md}`,
            background: colors.card,
            color: colors.textMuted,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.sm,
            fontSize: typography.fontSize.sm,
            fontFamily: typography.fontFamily.mono,
            cursor: 'pointer',
          }}
        >
          Copiar detalles tecnicos
        </button>

        <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.sm }}>
          <button
            onClick={this.handleRetry}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: colors.primary,
              color: colors.textPrimary,
              border: 'none',
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.medium,
              fontFamily: typography.fontFamily.sans,
              cursor: 'pointer',
            }}
          >
            Reintentar
          </button>

          {this.props.onRemove && (
            <button
              onClick={this.handleRemove}
              style={{
                padding: `${spacing.sm} ${spacing.md}`,
                background: colors.card,
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.sm,
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                fontFamily: typography.fontFamily.sans,
                cursor: 'pointer',
              }}
            >
              Cerrar pestana
            </button>
          )}
        </div>

        <div style={{
          fontSize: typography.fontSize.sm,
          color: colors.textMuted,
          textAlign: 'center',
          fontFamily: typography.fontFamily.sans,
        }}>
          El Shell sigue funcionando. Usa Ctrl+K para abrir otra aplicacion.
        </div>
      </div>
    )
  }
}
